import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { PolymerType } from '@rti/shared';
import { useTheme } from '@/contexts/ThemeContext';

interface PolymerSelectorProps {
  polymers: Record<PolymerType, string>;
  selectedPolymer: PolymerType;
  onPolymerChange: (polymer: PolymerType) => void;
  polymerCategory: 'PE' | 'PP' | 'PS' | 'PVC' | 'PET';
}

const PolymerSelector: React.FC<PolymerSelectorProps> = ({
  polymers,
  selectedPolymer,
  onPolymerChange,
  polymerCategory
}) => {
  const { isDark } = useTheme();
  const [isSmallBreakpoint, setIsSmallBreakpoint] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1179px)').matches
  );
  const [isTinyBreakpoint, setIsTinyBreakpoint] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  const [polymerNavIndex, setPolymerNavIndex] = useState(0);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1179px)');
    const tinyMq = window.matchMedia('(max-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsSmallBreakpoint(e.matches);
    const tinyHandler = (e: MediaQueryListEvent) => setIsTinyBreakpoint(e.matches);
    mq.addEventListener('change', handler);
    tinyMq.addEventListener('change', tinyHandler);
    return () => {
      mq.removeEventListener('change', handler);
      tinyMq.removeEventListener('change', tinyHandler);
    };
  }, []);

  const filteredPolymers = Object.entries(polymers)
    .filter(([_, label]) => {
      if (polymerCategory === 'PE') {
        return !label.includes('PP') && !label.includes('PVC') && !label.includes('PET') && !label.includes('PS');
      } else if (polymerCategory === 'PP') {
        return label.includes('PP') && !label.includes('PS');
      } else if (polymerCategory === 'PS') {
        return label.includes('PS');
      } else if (polymerCategory === 'PVC') {
        return label.includes('PVC');
      } else if (polymerCategory === 'PET') {
        return label.includes('PET');
      }
      return false;
    });

  const buttonsToShow = isTinyBreakpoint ? 1 : (isSmallBreakpoint && polymerCategory !== 'PS') ? 2 : filteredPolymers.length;
  const visibleButtons = filteredPolymers.slice(polymerNavIndex, polymerNavIndex + buttonsToShow);
  const maxIndex = Math.max(0, filteredPolymers.length - buttonsToShow);

  // Adjust chevron index when selected polymer changes to keep it visible
  useEffect(() => {
    const selectedIndex = filteredPolymers.findIndex(([key]) => key === selectedPolymer);
    if (selectedIndex !== -1) {
      const currentButtonsToShow = isTinyBreakpoint ? 1 : isSmallBreakpoint ? 2 : filteredPolymers.length;
      // If selected polymer is not in visible range, adjust index
      if (selectedIndex < polymerNavIndex) {
        setPolymerNavIndex(selectedIndex);
      } else if (selectedIndex >= polymerNavIndex + currentButtonsToShow) {
        setPolymerNavIndex(Math.max(0, selectedIndex - currentButtonsToShow + 1));
      }
    }
  }, [selectedPolymer, filteredPolymers, polymerNavIndex, isTinyBreakpoint, isSmallBreakpoint]);

  return (
    isSmallBreakpoint && filteredPolymers.length > 0 && polymerCategory !== 'PS' && polymerCategory !== 'PVC' && polymerCategory !== 'PET' ? (
      // Below 1180px: chevron navigation with 2 buttons (750px-1180px) or 1 button (below 750px)
      <div className={`flex ${isTinyBreakpoint ? 'flex-col items-center' : 'items-center'} gap-0 mb-7`}>
        <div className={`flex items-center gap-0 ${isTinyBreakpoint ? 'gap-1' : ''}`}>
          <button
            onClick={() => setPolymerNavIndex(Math.max(0, polymerNavIndex - 1))}
            disabled={polymerNavIndex === 0}
            className={`p-1 rounded transition-colors ${isDark ? 'text-rs-green hover:bg-rs-jet' : 'text-black hover:bg-rs-off-black/20'} disabled:opacity-40`}
            aria-label="Previous polymer"
          >
            <ChevronLeft size={28} strokeWidth={2.5} />
          </button>

          <div className={`flex gap-2 ${isTinyBreakpoint ? 'flex-col items-center' : ''}`}>
            {visibleButtons.map(([key, label]) => (
              <button
                key={key}
                data-testid={`polymer-button-${key}`}
                onClick={() => onPolymerChange(key as PolymerType)}
                className={`px-3 py-3 rounded-sm text-sm transition-colors ${
                  selectedPolymer === key
                    ? 'bg-rs-green !text-black'
                    : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <button
            onClick={() => setPolymerNavIndex(Math.min(maxIndex, polymerNavIndex + buttonsToShow))}
            disabled={polymerNavIndex >= maxIndex}
            className={`p-1 rounded transition-colors ${isDark ? 'text-rs-green hover:bg-rs-jet' : 'text-black hover:bg-rs-off-black/20'} disabled:opacity-40`}
            aria-label="Next polymer"
          >
            <ChevronRight size={28} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    ) : (
      // 1180px and above: show all polymers
      <div className={`polymer-selector flex gap-2 mb-7 flex-wrap ${(polymerCategory === 'PS' || polymerCategory === 'PVC' || polymerCategory === 'PET') && isTinyBreakpoint ? 'justify-center' : ''}`}>
        {filteredPolymers.map(([key, label]) => (
          <button
            key={key}
            data-testid={`polymer-button-${key}`}
            onClick={() => onPolymerChange(key as PolymerType)}
            className={`p-3 rounded-sm text-sm mb-2 transition-colors ${
              selectedPolymer === key
                ? 'bg-rs-green !text-black'
                : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    )
  );
};

export default PolymerSelector;
